import type { ChangeEvent } from 'react';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import type { RadioChangeEvent } from 'antd';
import {
  Button,
  Descriptions,
  Divider,
  Form,
  Input,
  Radio,
  Space,
  Switch,
  Typography,
  message,
  DatePicker,
  Grid,
} from 'antd';
import { FormItemRadioButtonGroup } from './FormItemRadioButtonGroupComponent';
import { FormItemSelectGroup } from './FormItemSelectGroupComponent';
import type {
  EvaluationReportForComputerScience,
  GroupValue,
  InternshipProcessWithNames,
  InternshipWorkLogEntries,
} from '../../Types/interfaces';
import { PaperWorkLanguage } from '../../Types/interfaces';
import { StudyLineType } from '../../Types/interfaces';
import TextArea from 'antd/es/input/TextArea';
import { useTranslation } from 'react-i18next';
import {
  AddEvaluationReport,
  GetOrganizationById,
  GetWorkLogEntries,
} from '../../helper/apiHelper';
import Compact from 'antd/es/space/Compact';
import type { RuleObject } from 'antd/es/form';
import {
  calcKnowledgePoints,
  calcSoftSkillPoints,
  formatNumberWithMaxDecimals,
} from './utils/calculations';

const { useBreakpoint } = Grid;

export interface EvaluationComponentProps {
  process: InternshipProcessWithNames;
  evaluationFinished: () => void;
}

type IncludedActivitiesSwitchState = {
  includedTraining: boolean;
  includedSoftDev: boolean;
  includedDevOfSoftSkills: boolean;
  includedEvaluation: boolean;
};

export const EvaluationComponentForComputerScience = (
  props: EvaluationComponentProps
): JSX.Element => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [evalReport, setEvalReport] =
    useState<EvaluationReportForComputerScience>();
  const [totalPoints, setTotalPoints] = useState<string>('0');
  const [mainActivity, setMainActivity] = useState<string>('');
  const [otherMainActivity, setOtherMainActivity] = useState<string>('');
  const [otherIncludedActivities, setOtherIncludedActivities] =
    useState<string>('');
  const { process, evaluationFinished } = props;
  const screens = useBreakpoint();

  const [includedActivitiesSwitches, setIncludedActivitiesSwitches] =
    useState<IncludedActivitiesSwitchState>({
      includedTraining: false,
      includedSoftDev: false,
      includedDevOfSoftSkills: false,
      includedEvaluation: false,
    });

  const [language, setLanguage] = useState<PaperWorkLanguage>();

  const handleMainActivityRadioChange = (e: RadioChangeEvent): void => {
    const selectedActivity = e.target.value;
    if (selectedActivity === mainActivity) {
      setMainActivity('');
    } else {
      setMainActivity(selectedActivity);
    }
    setOtherMainActivity('');
  };

  const handleMainActivityTextAreaChange = (
    e: ChangeEvent<HTMLTextAreaElement>
  ): void => {
    const { value } = e.target;
    if (value.trim() !== '') {
      setMainActivity('');
    }
    setOtherMainActivity(value);
  };

  const getFormValues = useCallback(() => {
    const regNum = form.getFieldValue('registrationNumber');
    //const day = form.getFieldValue('date');
    const dayObj = form.getFieldValue('date'); // Dayjs | null
    const day = dayObj ? dayObj.format('YYYY-MM-DD') : undefined; // string | undefined
    // Cap I.
    const knowledgeAtStart = form.getFieldValue('knowledgeAtStart');
    const qualityOfActivities = form.getFieldValue('qualityOfActivities');
    const qualityOfDocumentation = form.getFieldValue('qualityOfDocumentation');
    const gainedKnowledge = form.getFieldValue('gainedKnowledge');

    // Cap II.
    const complyingWithTasks = form.getFieldValue('complyingWithTasks');
    const respectingDeadlines = form.getFieldValue('respectingDeadlines');
    const qualities = form.getFieldValue('qualities');

    return {
      regNum,
      day,
      knowledgeAtStart,
      qualityOfActivities,
      qualityOfDocumentation,
      gainedKnowledge,
      complyingWithTasks,
      respectingDeadlines,
      qualities,
    };
  }, [form]);

  const findFirstAndLastDate = (
    workLogEntries: InternshipWorkLogEntries[]
  ): { firstDate: Date; lastDate: Date } | null => {
    if (workLogEntries.length === 0) {
      return null;
    }

    let firstDate = new Date(workLogEntries[0].startTime);
    let lastDate = new Date(workLogEntries[0].endTime);

    for (let i = 1; i < workLogEntries.length; i++) {
      const entry = workLogEntries[i];
      const startTime = new Date(entry.startTime);
      const endTime = new Date(entry.endTime);

      if (startTime < firstDate) {
        firstDate = startTime;
      }
      if (endTime > lastDate) {
        lastDate = endTime;
      }
    }

    return { firstDate, lastDate };
  };

  useEffect(() => {
    (async (): Promise<void> => {
      const orgResp = await GetOrganizationById(
        process.organizationId.toString()
      );
      setLanguage(orgResp.language);
    })();
  }, [process]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const wlEntries = await GetWorkLogEntries(process.workLogId);
      const dates = findFirstAndLastDate(wlEntries);

      const values = getFormValues();

      setEvalReport({
        evaluationDocumentUrl: '',
        studyLineType: StudyLineType.ComputerScience,

        number: values.regNum,
        of: values.day,
        internshipStarted: dates?.firstDate ?? '',
        internshipCompleted: dates?.lastDate ?? '',

        organizationMainAcitvityIsSoftwareDevelopment:
          mainActivity === 'mainActivitySoftwareDev',
        organizationMainAcitvityIsSoftwareDevelopmentUtilization:
          mainActivity === 'mainActivitySoftwareUtil',
        organizationMainAcitvityIsITServicesAndEquipment:
          mainActivity === 'mainActivityIsITServicesAndEquipment',
        otherMainActivity: otherMainActivity,

        internshipIncludedTraining: includedActivitiesSwitches.includedTraining,
        internshipIncludedSoftwareDevelopment:
          includedActivitiesSwitches.includedSoftDev,
        internshipIncludedDevelopmentOfSoftSkills:
          includedActivitiesSwitches.includedDevOfSoftSkills,
        internshipIncludedEvaluation:
          includedActivitiesSwitches.includedEvaluation,
        otherActvitiesIncludedInInternship: otherIncludedActivities,

        studentsKnowledgeWhenStarting: values.knowledgeAtStart,
        qualityOfWork: values.qualityOfActivities,
        qualityOfDocumentation: values.qualityOfDocumentation,
        pointsForAlgorithmsAndDataStructures:
          values.gainedKnowledge['algorithmsAndDataStructures'],
        pointsForProgrammingLanguagesAndEnvironments:
          values.gainedKnowledge['programmingLanguages'],
        pointsForSoftwareArchitectures:
          values.gainedKnowledge['softwareArchitecture'],
        pointsForSoftwareMethodologies:
          values.gainedKnowledge['softwareMethodologies'],
        pointsForDatabases: values.gainedKnowledge['databases'],
        pointsForOperatingSystemsAndComputerNetworks:
          values.gainedKnowledge['opSystemsAndComputerNetworks'],
        pointsForTesting: values.gainedKnowledge['testing'],
        pointsForSecurity: values.gainedKnowledge['security'],
        pointsForOtherDevelopmentSkills:
          values.gainedKnowledge['otherValue'] === undefined
            ? 0
            : values.gainedKnowledge['otherValue'],
        otherDevelopmentSkills:
          values.gainedKnowledge['otherTitle'] === undefined
            ? ''
            : values.gainedKnowledge['otherTitle'],

        taskAdherence: values.complyingWithTasks,
        terminologyUsage: values.respectingDeadlines,
        pointsForBackground: values.qualities['background'],
        pointsForTeamWork: values.qualities['teamwork'],
        pointsForResponsibility: values.qualities['responsability'],
        pointsForInitiative: values.qualities['initiative'],
        pointsForEffortCapacity: values.qualities['effort'],
        pointsForAdaptationCapacity:
          values.qualities['abilityToAdaptAndImprove'],
        pointsForOtherSoftSkills:
          values.qualities['otherValue'] === undefined
            ? 0
            : values.qualities['otherValue'],
        otherSoftSkills:
          values.qualities['otherTitle'] === undefined
            ? ''
            : values.qualities['otherTitle'],
      });
    };
    fetchData();
  }, [
    getFormValues,
    includedActivitiesSwitches.includedDevOfSoftSkills,
    includedActivitiesSwitches.includedEvaluation,
    includedActivitiesSwitches.includedSoftDev,
    includedActivitiesSwitches.includedTraining,
    mainActivity,
    otherIncludedActivities,
    otherMainActivity,
    process.workLogId,
  ]);

  const handleIncludedActivitiesSwitchChange = (
    name: keyof IncludedActivitiesSwitchState
  ): void => {
    setIncludedActivitiesSwitches({
      ...includedActivitiesSwitches,
      [name]: !includedActivitiesSwitches[name],
    });
  };

  const handleIncludedActivitiesTextAreaChange = (
    e: ChangeEvent<HTMLTextAreaElement>
  ): void => {
    const { value } = e.target;
    setOtherIncludedActivities(value);
  };

  const myValidators = [
    {
      validator:
        (maxCategories: number | undefined) =>
        (_: unknown, value: GroupValue): Promise<void> => {
          const selectedCount = Object.values(value).filter(
            (val) => val !== 0 && val !== ''
          ).length;
          const hasOtherTitle =
            value['otherTitle'] !== undefined &&
            value['otherTitle'].trim() !== '';
          const hasOtherValue = !(
            value['otherValue'] === undefined || value['otherValue'] === 0
          );

          // Check if otherTitle and otherValue are both set, then allow up to 5 categories
          if (
            hasOtherTitle &&
            hasOtherValue &&
            maxCategories &&
            selectedCount > maxCategories + 1
          ) {
            return Promise.reject(
              new Error(
                t('evaluationComp.maxEvaluationsExceededWithOther') +
                  `(${maxCategories})`
              )
            );
          }
          // Otherwise, allow up to 4 categories
          else if (
            hasOtherTitle === false &&
            hasOtherValue === false &&
            maxCategories &&
            selectedCount > maxCategories
          ) {
            return Promise.reject(
              new Error(
                t('evaluationComp.maxEvaluationsExceeded') +
                  `(${maxCategories})`
              )
            );
          }

          return Promise.resolve();
        },
      message: t('evaluationComp.tooManyCategories'),
    },
    {
      validator:
        () =>
        (_: unknown, value: GroupValue): Promise<void> => {
          const hasOtherTitle = !(
            value['otherTitle'] === undefined ||
            value['otherTitle'].trim() === ''
          );
          const hasOtherValue = !(
            value['otherValue'] === undefined || value['otherValue'] === 0
          );

          if (
            (hasOtherTitle && !hasOtherValue) ||
            (hasOtherValue && !hasOtherTitle)
          ) {
            return Promise.reject(
              new Error(t('evaluationComp.titleValueMustNotBeEmtpy'))
            );
          } else if (hasOtherTitle && value['otherTitle']?.trim() === '') {
            return Promise.reject(
              new Error(t('evaluationComp.emptyOtherTitle'))
            );
          }

          return Promise.resolve();
        },
      message: t('evaluationComp.bothOtherFieldsIfAny'),
    },
  ];

  const validateMainActivity = async (
    _: RuleObject,
    value: string | null | undefined
  ): Promise<void> => {
    if (value !== undefined && value !== null && value !== '') {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t('evaluationComp.validateError')));
  };

  const onChange = async (): Promise<void> => {
    const values = getFormValues();

    const tmp: EvaluationReportForComputerScience = {
      evaluationDocumentUrl: '',
      studyLineType: StudyLineType.ComputerScience,

      number: values.regNum,
      of: values.day,
      internshipStarted: evalReport?.internshipStarted ?? '',
      internshipCompleted: evalReport?.internshipCompleted ?? '',

      organizationMainAcitvityIsSoftwareDevelopment:
        mainActivity === 'mainActivitySoftwareDev',
      organizationMainAcitvityIsSoftwareDevelopmentUtilization:
        mainActivity === 'mainActivitySoftwareUtil',
      organizationMainAcitvityIsITServicesAndEquipment:
        mainActivity === 'mainActivityIsITServicesAndEquipment',
      otherMainActivity: otherMainActivity,

      internshipIncludedTraining: includedActivitiesSwitches.includedTraining,
      internshipIncludedSoftwareDevelopment:
        includedActivitiesSwitches.includedSoftDev,
      internshipIncludedDevelopmentOfSoftSkills:
        includedActivitiesSwitches.includedDevOfSoftSkills,
      internshipIncludedEvaluation:
        includedActivitiesSwitches.includedEvaluation,
      otherActvitiesIncludedInInternship: otherIncludedActivities,

      studentsKnowledgeWhenStarting: values.knowledgeAtStart as number,
      qualityOfWork: values.qualityOfActivities as number,
      qualityOfDocumentation: values.qualityOfDocumentation as number,
      pointsForAlgorithmsAndDataStructures: values.gainedKnowledge[
        'algorithmsAndDataStructures'
      ] as number,
      pointsForProgrammingLanguagesAndEnvironments: values.gainedKnowledge[
        'programmingLanguages'
      ] as number,
      pointsForSoftwareArchitectures: values.gainedKnowledge[
        'softwareArchitectures'
      ] as number,
      pointsForSoftwareMethodologies: values.gainedKnowledge[
        'softwareMethodologies'
      ] as number,
      pointsForDatabases: values.gainedKnowledge['databases'] as number,
      pointsForOperatingSystemsAndComputerNetworks: values.gainedKnowledge[
        'opSystemsAndComputerNetworks'
      ] as number,
      pointsForTesting: values.gainedKnowledge['testing'] as number,
      pointsForSecurity: values.gainedKnowledge['security'] as number,
      pointsForOtherDevelopmentSkills:
        values.gainedKnowledge?.['otherValue'] || 0,
      otherDevelopmentSkills: values.gainedKnowledge?.['otherTitle'] || '',

      taskAdherence: values.complyingWithTasks as number,
      terminologyUsage: values.respectingDeadlines as number,
      pointsForBackground: values.qualities['background'] as number,
      pointsForTeamWork: values.qualities['teamwork'] as number,
      pointsForResponsibility: values.qualities['responsability'] as number,
      pointsForInitiative: values.qualities['initiative'] as number,
      pointsForEffortCapacity: values.qualities['effort'] as number,
      pointsForAdaptationCapacity: values.qualities[
        'abilityToAdaptAndImprove'
      ] as number,
      pointsForOtherSoftSkills: values.qualities?.['otherValue'] || 0,
      otherSoftSkills: values.qualities?.['otherTitle'] || '',
    };

    setEvalReport(tmp);
    setTotalPoints(
      formatNumberWithMaxDecimals(
        calcKnowledgePoints(tmp) + calcSoftSkillPoints(tmp)
      )
    );
  };

  const onFinish = async (): Promise<void> => {
    try {
      if (!evalReport) {
        return;
      }
      const evaluationReport: EvaluationReportForComputerScience = evalReport;
      await AddEvaluationReport(process.id, evaluationReport);
      evaluationFinished();
      messageApi.success({
        content: t('evaluationComp.addSuccess'),
      });
      form.resetFields();
    } catch (error) {
      messageApi.error({
        content: t('evaluationComp.addError') + error,
      });
    }
  };

  const onFinishFailed = async (): Promise<void> => {
    messageApi.error(t('evaluationComp.incorrectForm'));
  };

  return (
    <>
      {contextHolder}
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onChange={onChange}
        labelWrap={true}
        initialValues={{ otherMainActivity: '' }}
      >
        <Typography.Title level={3}>
          {t('evaluationComp.regNumber')}
        </Typography.Title>
        <Space direction={screens.xs ? 'vertical' : 'horizontal'}>
          <Form.Item
            name="registrationNumber"
            label={t('evaluationComp.regNumberLabel')}
            colon={false}
            rules={[
              {
                required: true,
                message: t('evaluationComp.inputRegNumber'),
              },
            ]}
            extra={t('evaluationComp.enterRegistrationNumber')}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="date"
            label={t('evaluationComp.date')}
            colon={false}
            rules={[
              {
                required: true,
                message: t('evaluationComp.inputDate'),
              },
            ]}
            extra={t('evaluationComp.enterRegistrationDate')}
          >
            <DatePicker format="YYYY-MM-DD" />
          </Form.Item>
        </Space>

        <Typography.Title level={3}>
          {t('evaluationComp.mainActivity') + ':'}
        </Typography.Title>
        <Form.Item
          name="mainActivity"
          rules={[{ validator: validateMainActivity }]}
        >
          <Radio.Group
            onChange={handleMainActivityRadioChange}
            value={mainActivity}
            style={{
              display: 'flex',
              flexDirection: screens.xs ? 'column' : 'row',
            }}
          >
            <Radio value="mainActivitySoftwareDev">
              {t('evaluationComp.softDev')}
            </Radio>
            <Radio value="mainActivitySoftwareUtil">
              {t('evaluationComp.softUtil')}
            </Radio>
            <Radio value="mainActivityIsITServicesAndEquipment">
              {t('evaluationComp.itServ')}
            </Radio>
            <Radio value="other">{t('evaluationComp.other')}</Radio>
          </Radio.Group>
        </Form.Item>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px' }}>
            {t('evaluationComp.other') + ':'}
          </span>
          <TextArea
            name="otherMainActivity"
            rows={1}
            value={otherMainActivity}
            onChange={handleMainActivityTextAreaChange}
            disabled={!!mainActivity && mainActivity !== 'other'}
            style={{ maxWidth: 400 }}
          />
        </div>

        <Typography.Title level={3}>
          {t('evaluationComp.activityTypes') + ':'}
        </Typography.Title>
        <Form.Item name="includedTraining" valuePropName="checked">
          <div>
            <Switch
              onChange={() =>
                handleIncludedActivitiesSwitchChange('includedTraining')
              }
              checked={includedActivitiesSwitches.includedTraining}
            />
            <Button
              onClick={() =>
                handleIncludedActivitiesSwitchChange('includedTraining')
              }
            >
              {' '}
              {t('evaluationComp.training')}{' '}
            </Button>
          </div>
        </Form.Item>

        <Form.Item name="includedSoftwareDev" valuePropName="checked">
          <div>
            <Switch
              onChange={() =>
                handleIncludedActivitiesSwitchChange('includedSoftDev')
              }
              checked={includedActivitiesSwitches.includedSoftDev}
            />
            <Button
              onClick={() =>
                handleIncludedActivitiesSwitchChange('includedSoftDev')
              }
            >
              {' '}
              {t('evaluationComp.softDev')}
            </Button>
          </div>
        </Form.Item>

        <Form.Item name="includedDevOfSoftSkills" valuePropName="checked">
          <div>
            <Switch
              onChange={() =>
                handleIncludedActivitiesSwitchChange('includedDevOfSoftSkills')
              }
              checked={includedActivitiesSwitches.includedDevOfSoftSkills}
            />
            <Button
              onClick={() =>
                handleIncludedActivitiesSwitchChange('includedDevOfSoftSkills')
              }
            >
              {' '}
              {t('evaluationComp.devOfSoftSkills')}
            </Button>
          </div>
        </Form.Item>

        <Form.Item name="includedEvaluation" valuePropName="checked">
          <div>
            <Switch
              onChange={() =>
                handleIncludedActivitiesSwitchChange('includedEvaluation')
              }
              checked={includedActivitiesSwitches.includedEvaluation}
            />
            <Button
              onClick={() =>
                handleIncludedActivitiesSwitchChange('includedEvaluation')
              }
            >
              {' '}
              {t('evaluationComp.evaluation')}
            </Button>
          </div>
        </Form.Item>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px' }}>
            {t('evalReportDisplay.other') + ':'}
          </span>
          <TextArea
            name="otherIncludedActivities"
            rows={1}
            value={otherIncludedActivities}
            onChange={handleIncludedActivitiesTextAreaChange}
            style={{ maxWidth: 400 }}
          />
        </div>

        <Typography.Title level={3}>
          {t('evalReportDisplay.specKnowledge')}
        </Typography.Title>
        <FormItemRadioButtonGroup
          t={t}
          name="knowledgeAtStart"
          title={t('evalReportDisplay.knowledgeAtStart')}
          options={[
            { value: 0.1, label: t('evalReportDisplay.poor') },
            { value: 0.2, label: t('evalReportDisplay.satisfactory') },
            { value: 0.3, label: t('evalReportDisplay.good') },
            { value: 0.4, label: t('evalReportDisplay.vgood') },
            { value: 0.5, label: t('evalReportDisplay.excellent') },
          ]}
        />

        <FormItemRadioButtonGroup
          t={t}
          name="qualityOfActivities"
          title={t('evalReportDisplay.qoa')}
          options={[
            { value: 0.1, label: t('evalReportDisplay.poor') },
            { value: 0.2, label: t('evalReportDisplay.satisfactory') },
            { value: 0.3, label: t('evalReportDisplay.good') },
            { value: 0.4, label: t('evalReportDisplay.vgood') },
            { value: 0.5, label: t('evalReportDisplay.excellent') },
          ]}
        />

        <FormItemRadioButtonGroup
          t={t}
          name="qualityOfDocumentation"
          title={t('evalReportDisplay.qod')}
          options={[
            { value: 0, label: t('evalReportDisplay.notcase') },
            { value: 0.1, label: t('evalReportDisplay.superficial') },
            { value: 0.25, label: t('evalReportDisplay.incomplete') },
            { value: 0.5, label: t('evalReportDisplay.complete') },
          ]}
        />

        <FormItemSelectGroup
          t={t}
          name="gainedKnowledge"
          title={t('evaluationComp.gainedKnow')}
          subjects={[
            {
              name: 'algorithmsAndDataStructures',
              title: t('evalReportDisplay.algsAndDS'),
            },
            {
              name: 'programmingLanguages',
              title: t('evalReportDisplay.langsAndDevEnvs'),
            },
            {
              name: 'softwareArchitectures',
              title: t('evalReportDisplay.softArch'),
            },
            {
              name: 'softwareMethodologies',
              title: t('evalReportDisplay.softMethods'),
            },
            {
              name: 'opSystemsAndComputerNetworks',
              title: t('evalReportDisplay.opsys'),
            },
            { name: 'databases', title: t('evalReportDisplay.databases') },
            { name: 'testing', title: t('evalReportDisplay.testing') },
            { name: 'security', title: t('evalReportDisplay.security') },
          ]}
          options={[
            { value: 0, label: t('evalReportDisplay.noteval') },
            { value: 0.3, label: t('evalReportDisplay.vPoor') },
            { value: 0.5, label: t('evalReportDisplay.satisfactory') },
            { value: 0.7, label: t('evalReportDisplay.good') },
            { value: 0.9, label: t('evalReportDisplay.vgood') },
          ]}
          hasOther={true}
          validators={[
            {
              validator: myValidators[0].validator(5),
              message: myValidators[0].message,
            },
            {
              validator: myValidators[1].validator(undefined),
              message: myValidators[1].message,
            },
          ]}
          maxCategories={5}
          onChange={onChange}
        />

        <Typography.Title level={3}>
          {t('evalReportDisplay.softSkills')}
        </Typography.Title>
        <FormItemRadioButtonGroup
          t={t}
          name="complyingWithTasks"
          title={t('evalReportDisplay.compWithTasks')}
          options={[
            { value: 0, label: t('evalReportDisplay.notall') },
            { value: 0.2, label: t('evalReportDisplay.smallExtent') },
            { value: 0.4, label: t('evalReportDisplay.largeExtent') },
            { value: 0.5, label: t('evalReportDisplay.completely') },
          ]}
        />

        <FormItemRadioButtonGroup
          t={t}
          name="respectingDeadlines"
          title={t('evalReportDisplay.respectDeadlines')}
          options={[
            { value: 0, label: t('evalReportDisplay.notall') },
            { value: 0.2, label: t('evalReportDisplay.smallExtent') },
            { value: 0.4, label: t('evalReportDisplay.largeExtent') },
            { value: 0.5, label: t('evalReportDisplay.completely') },
          ]}
        />

        <FormItemSelectGroup
          t={t}
          name="qualities"
          title={t('evaluationComp.qualities')}
          subjects={[
            ...(language === PaperWorkLanguage.English
              ? [
                  {
                    name: 'background',
                    title: t('evalReportDisplay.background'),
                  },
                ]
              : []),
            { name: 'teamwork', title: t('evalReportDisplay.teamwork') },
            { name: 'responsability', title: t('evalReportDisplay.resp') },
            { name: 'initiative', title: t('evalReportDisplay.initiative') },
            { name: 'effort', title: t('evalReportDisplay.effort') },
            {
              name: 'abilityToAdaptAndImprove',
              title: t('evalReportDisplay.adaptAndImprove'),
            },
          ]}
          options={[
            { value: 0, label: t('evalReportDisplay.noteval') },
            { value: 0.5, label: t('evalReportDisplay.demonstrated') },
          ]}
          hasOther={true}
          validators={[
            {
              validator: myValidators[0].validator(4),
              message: myValidators[0].message,
            },
            {
              validator: myValidators[1].validator(undefined),
              message: myValidators[1].message,
            },
          ]}
          maxCategories={4}
          onChange={onChange}
        />

        <Compact>
          <Descriptions bordered>
            <Descriptions.Item
              label={<strong>{t('evaluationComp.grade')}</strong>}
            >
              1 + {totalPoints} = {1 + Number(totalPoints)}
            </Descriptions.Item>
          </Descriptions>
        </Compact>

        <Divider />

        <Form.Item wrapperCol={{ span: 24 }}>
          <Button type="primary" block htmlType="submit">
            {t('evaluationComp.submit')}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};
